import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import { Select, Input, Button, Form, message, Table, Popconfirm, Modal, Tag, Typography } from 'antd';
import coreAPIService from '../apiServices/coreAPIService';
import orgManagementService from '../apiServices/orgManagementService';
import { useAuthOrg } from "../context/AuthOrgContext";
import { CTW_NODE_API_ENDPOINT } from '../utils';
import { OrgWabaDetails, WabaDetail } from "../interfaces/setup";
import '../assets/css/tablesShadow.css'

interface Bot {
    botId: string;
    botName: string;
    status: number;
    clientId: string;
}

interface SourceMapping {
    botId: string;
    sourceName: string;
    sourceText: string;
}

interface SourceMappingWithId extends SourceMapping {
    id: string;
}

const SourceMapping = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const { availableOrgs, currentOrg } = useAuthOrg();

    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(currentOrg?.orgId || null);
    const [orgWabaData, setOrgWabaData] = useState<OrgWabaDetails[]>([]);
    const [wabaList, setWabaList] = useState<WabaDetail[]>([]);
    const [selectedWaba, setSelectedWaba] = useState<string | null>(null);
    const [bots, setBots] = useState<Bot[]>([]);
    const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
    const [sourceMappings, setSourceMappings] = useState<SourceMappingWithId[]>([]);
    const [loading, setLoading] = useState(false);
    const [wabaLoading, setWabaLoading] = useState(false);
    const [botsLoading, setBotsLoading] = useState(false);
    const [mappingsLoading, setMappingsLoading] = useState(false);

    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);

    // Fetch all WABA details on mount
    useEffect(() => {
        const fetchAllWabaDetails = async () => {
            setWabaLoading(true);
            try {
                const res = await orgManagementService.getWabaDetails();
                if (res?.meta?.code === 200 && res.data) {
                    setOrgWabaData(res.data);
                }
            } catch (error) {
                console.error("Failed to fetch WABA details", error);
            } finally {
                setWabaLoading(false);
            }
        };
        fetchAllWabaDetails();
    }, []);

    // Update WABA list when Account changes
    useEffect(() => {
        if (selectedOrgId && orgWabaData.length > 0) {
            const orgData = orgWabaData.find(org => org.orgId === selectedOrgId);
            if (orgData) {
                setWabaList(orgData.wabaDetails);
                // Auto-select first WABA if available
                if (orgData.wabaDetails.length > 0) {
                    setSelectedWaba(orgData.wabaDetails[0].wabaNumber);
                } else {
                    setSelectedWaba(null);
                }
            } else {
                setWabaList([]);
                setSelectedWaba(null);
            }
        } else {
            setWabaList([]);
            setSelectedWaba(null);
        }
        setSelectedBotId(null);
        setSourceMappings([]);
    }, [selectedOrgId, orgWabaData]);

    // Fetch Bots when WABA changes
    useEffect(() => {
        if (!selectedWaba) {
            setBots([]);
            setSelectedBotId(null);
            return;
        }

        const fetchBots = async () => {
            const wabaItem = wabaList.find(w => w.wabaNumber === selectedWaba);
            if (!wabaItem) return;

            try {
                setBotsLoading(true);
                const response: any = await coreAPIService.get(
                    CTW_NODE_API_ENDPOINT,
                    `/common/v1/bots/${wabaItem.clientId}`
                );

                if (response.meta.success && response.meta.code === 200) {
                    const botList = response.data || [];
                    setBots(botList);

                    // Auto-select first active bot
                    const firstActive = botList.find((bot: Bot) => bot.status === 1);
                    const defaultBot = firstActive || botList[0];
                    if (defaultBot) {
                        setSelectedBotId(defaultBot.botId);
                    } else {
                        setSelectedBotId(null);
                    }
                }
            } catch (error) {
                message.error('Failed to fetch bots for this WABA');
            } finally {
                setBotsLoading(false);
            }
        };

        fetchBots();
    }, [selectedWaba, wabaList]);

    // Fetch Source Mappings when Bot changes
    useEffect(() => {
        if (selectedBotId && selectedWaba) {
            fetchSourceMappings();
        } else {
            setSourceMappings([]);
        }
    }, [selectedBotId, selectedWaba]);

    const fetchSourceMappings = async () => {
        if (!selectedBotId) return;

        const wabaItem = wabaList.find(w => w.wabaNumber === selectedWaba);
        if (!wabaItem) return;

        try {
            setMappingsLoading(true);
            const response: any = await coreAPIService.get(
                CTW_NODE_API_ENDPOINT,
                `/common/v1/source-mapping/${wabaItem.clientId}?bot_id=${selectedBotId}`
            );
            if (response.meta.success) {
                setSourceMappings(response.data || []);
            }
        } catch (error) {
            message.error('Failed to fetch source mappings');
        } finally {
            setMappingsLoading(false);
        }
    };

    const handleWabaChange = (wabaNumber: string) => {
        setSelectedWaba(wabaNumber);
        setSelectedBotId(null);
        setSourceMappings([]);
    };

    const handleDelete = async (id: string) => {
        const wabaItem = wabaList.find(w => w.wabaNumber === selectedWaba);
        if (!wabaItem) return;

        try {
            const response: any = await coreAPIService.delete(
                CTW_NODE_API_ENDPOINT,
                `/common/v1/source-mapping/${wabaItem.clientId}/${id}`
            );
            if (response.meta.success) {
                message.success('Source mapping deleted');
                fetchSourceMappings();
            } else {
                message.error(response.meta.message || 'Delete failed');
            }
        } catch (error) {
            message.error('Failed to delete');
        }
    };

    const handleSubmit = async (values: SourceMapping) => {
        const wabaItem = wabaList.find(w => w.wabaNumber === selectedWaba);
        if (!wabaItem || !selectedBotId) {
            message.error("Please select WABA and Bot");
            return;
        }

        try {
            setLoading(true);
            const payload = {
                ...values,
                sourceText: values.sourceText.trim(),
                botId: selectedBotId
            };

            const response: any = await coreAPIService.post(
                CTW_NODE_API_ENDPOINT,
                `/common/v1/source-mapping/${wabaItem.clientId}`,
                payload
            );

            if (response.meta.success) {
                message.success('Source mapping created!');
                form.resetFields();
                setIsModalVisible(false);
                fetchSourceMappings();
            } else {
                message.error(response.meta.message || 'Failed to save');
            }
        } catch (error: any) {
            message.error(error.message || 'Failed to create mapping');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Source Name',
            dataIndex: 'sourceName',
            key: 'sourceName',
        },
        {
            title: 'Source Text',
            dataIndex: 'sourceText',
            key: 'sourceText',
            ellipsis: true,
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: SourceMappingWithId) => (
                <Popconfirm
                    title="Delete this mapping?"
                    onConfirm={() => handleDelete(record.id)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Button type="link" danger>Delete</Button>
                </Popconfirm>
            ),
        },
    ];

    useEffect(() => {
        setBreadcrumbs([
            { title: <div className="text-lg cursor-pointer" onClick={() => navigate("/settings")}>Settings</div> },
            { title: <div className="text-lg">Source Mapping</div> },
        ]);
    }, [setBreadcrumbs, navigate]);

    return (
        <section className="px-10 py-8 flex flex-col gap-8">
            <div className="bg-white shadow-md rounded-xl p-8">
                {/* Account + WABA + Bot Selector */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <Typography.Text strong className="text-gray-500 text-xs mb-2">Select Account</Typography.Text>
                        <Select
                            placeholder="Choose Account"
                            className="w-full"
                            value={selectedOrgId}
                            onChange={(val) => setSelectedOrgId(val)}
                            showSearch
                            optionFilterProp="children"
                        >
                            {availableOrgs.map((org) => (
                                <Select.Option key={org.orgId} value={org.orgId}>
                                    {org.orgName}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    <div>
                        <Typography.Text strong className="text-gray-500 text-xs mb-2">Select WhatsApp Business Account</Typography.Text>
                        <Select
                            placeholder="Choose WABA"
                            className="w-full"
                            value={selectedWaba}
                            onChange={handleWabaChange}
                            loading={wabaLoading}
                            disabled={!selectedOrgId}
                            showSearch={true}
                            virtual={false}
                        >
                            {wabaList.map((waba) => (
                                <Select.Option key={waba.wabaNumber} value={waba.wabaNumber}>
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium">{waba.wabaNumber}</span>
                                        <Tag color="blue">{waba.subdomain}</Tag>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    <div>
                        <Typography.Text strong className="text-gray-500 text-xs mb-2">Select Bot</Typography.Text>
                        <Select
                            placeholder="Choose a bot"
                            className="w-full"
                            value={selectedBotId || undefined}
                            onChange={setSelectedBotId}
                            loading={botsLoading}
                            disabled={!selectedWaba}
                        >
                            {bots.map((bot) => (
                                <Select.Option key={bot.botId} value={bot.botId}>
                                    <div className="flex items-center gap-2">
                                        <span>{bot.botName}</span>
                                        <Tag color={bot.status === 1 ? 'green' : 'red'}>
                                            {bot.status === 1 ? 'Active' : 'Inactive'}
                                        </Tag>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </div>

                {/* Source Mappings Table */}
                <div className="mt-10">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-semibold">Source Mappings</h2>
                        <Button type="primary" className="bg-[#1F344C]" onClick={() => setIsModalVisible(true)} disabled={!selectedBotId}>
                            + Create New Mapping
                        </Button>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={sourceMappings}
                        rowKey="id"
                        loading={mappingsLoading}
                        pagination={{ pageSize: 10 }}
                        locale={{ emptyText: "No source mappings yet" }}
                    />
                </div>

                {!selectedOrgId && (
                    <div className="text-center py-12 text-gray-500">
                        Please select an Account to continue
                    </div>
                )}

                {selectedOrgId && !selectedWaba && (
                    <div className="text-center py-12 text-gray-500">
                        Please select a WhatsApp Business Account to continue
                    </div>
                )}
            </div>

            {/* Create Modal */}
            <Modal
                title="Create Source Mapping"
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="sourceName"
                        label="Source Name"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Input placeholder="e.g. Google Ads" />
                    </Form.Item>

                    <Form.Item
                        name="sourceText"
                        label="Source Text (exact match)"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Input.TextArea rows={4} placeholder="e.g. gclid=abc123" />
                    </Form.Item>

                    <Form.Item className="text-center mt-6">
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Save Mapping
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </section>
    );
};

export default SourceMapping;